import moment from 'moment-timezone';
import { getImpersonationFromQuery } from 'utils/url';
import {
    AVAILABLE_FIELDS_LIST,
    BASIC_DEVICE_ID_TYPES,
    BASIC_POSTBACK_TYPES_BLOCKLIST,
    DEFAULT_LOG_TYPE,
    EXCLUDE,
    EXPORT_TYPE_MAPPING,
    FPS_DEVICE_ID_TYPES,
    FPS_LOG_TYPE,
    INCLUDE,
    INSTALLS_LOG_TYPE,
    POSTBACKS_LOG_TYPE,
    PRESET_FIELDS_LIST,
    SELECTED_FIELDS_LIST,
    SKAN_POSTBACK_LOG_TYPE,
    TRINO_API_TYPE,
} from './constants';

export const setSubtraction = (arr1 = [], arr2 = []) => {
    const excluded = new Set(arr2);
    return arr1.filter(item => !excluded.has(item));
};

export const setIntersection = (arr1 = [], arr2 = []) => {
    const included = new Set(arr2);
    return arr1.filter(item => included.has(item));
};

export const getOperationOptions = () => [INCLUDE, EXCLUDE].map(op => ({ name: op, display_name: op }));

export const optionize = (items = [], labelKey = 'name', valueKey = labelKey) =>
    items.map(item => ({
        ...item,
        value: item[valueKey],
        label: item[labelKey],
    }));

export const getLogTypeLabelKey = logType => `STATIC.PAGES.EXPORT_LOGS.TYPES.${logType}`;

export const getConversionTypeLabelKey = conversionType =>
    `STATIC.PAGES.EXPORT_LOGS.CONVERSION_TYPES.${conversionType}`;

export const renameExportField = (exportFields, exportType, field) => {
    const mapping = exportFields?.renamed_fields?.[exportType] || {};
    return mapping[field] || field;
};

const fieldToDisplayObject = (exportFields, exportType, field) => ({
    field,
    display: renameExportField(exportFields, exportType, field),
});

const sortByDisplay = (a, b) => a.display.localeCompare(b.display, undefined, { sensitivity: 'base' });

export const buildFieldLists = responseValue => {
    const exportFields = { ...responseValue };
    const presetTypes = {};
    const exportLists = {};
    const adminExportLists = {};

    Object.keys(exportFields.available_fields || {}).forEach(exportType => {
        const selectedFields = [...(exportFields.selected_fields?.[exportType] || [])];
        const availableFields = exportFields.available_fields?.[exportType] || [];
        const userSelectedFields = setIntersection(selectedFields, availableFields);
        const unselectedFields = setSubtraction(availableFields, userSelectedFields);
        const exportTypePreset = exportFields.presets?.[exportType] || {};
        const defaultPresetFields = [...(exportTypePreset.default || [])];
        const adminAvailableFields = exportFields.admin_available_fields?.[exportType] || [];
        const adminUnselectedFields = setSubtraction(adminAvailableFields, defaultPresetFields);

        presetTypes[exportType] = Object.keys(exportTypePreset).map(presetType => ({
            text: presetType,
            value: presetType,
            label: presetType,
        }));

        exportLists[exportType] = {
            [AVAILABLE_FIELDS_LIST]: {
                filter: '',
                list: unselectedFields
                    .map(field => fieldToDisplayObject(exportFields, exportType, field))
                    .sort(sortByDisplay),
            },
            [SELECTED_FIELDS_LIST]: {
                filter: '',
                list: userSelectedFields.map(field => fieldToDisplayObject(exportFields, exportType, field)),
            },
        };

        const presetFields = {};
        Object.keys(exportTypePreset).forEach(presetType => {
            presetFields[presetType] = {
                filter: '',
                list: exportTypePreset[presetType].map(field => fieldToDisplayObject(exportFields, exportType, field)),
            };
        });

        adminExportLists[exportType] = {
            [AVAILABLE_FIELDS_LIST]: {
                filter: '',
                list: adminUnselectedFields
                    .map(field => fieldToDisplayObject(exportFields, exportType, field))
                    .sort(sortByDisplay),
            },
            [SELECTED_FIELDS_LIST]: {
                filter: '',
                list: defaultPresetFields.map(field => fieldToDisplayObject(exportFields, exportType, field)),
            },
            [PRESET_FIELDS_LIST]: presetFields,
        };
    });

    return {
        exportFields: {
            ...exportFields,
            lists: exportLists,
            adminLists: adminExportLists,
        },
        presetTypes,
    };
};

export const getFieldsList = (exportFields, adminMode, logType, listType) => {
    const exportType = EXPORT_TYPE_MAPPING[logType];
    if (!exportType) {
        return { filter: '', list: [] };
    }

    if (adminMode) {
        return exportFields?.adminLists?.[exportType]?.[listType] || { filter: '', list: [] };
    }

    return exportFields?.lists?.[exportType]?.[listType] || { filter: '', list: [] };
};

export const getSelectedFieldNames = (exportFields, adminMode, logType) =>
    getFieldsList(exportFields, adminMode, logType, SELECTED_FIELDS_LIST).list.map(fieldObject => fieldObject.field);

export const moveFieldBetweenLists = ({ sourceList, targetList, field, source, target, targetIndex }) => {
    const nextSourceList = sourceList.filter(item => item.field !== field.field);
    let insertAt = Number.isInteger(targetIndex) ? targetIndex : targetList.length;

    if (source === target) {
        const oldIndex = sourceList.findIndex(item => item.field === field.field);
        if (oldIndex !== -1 && insertAt > oldIndex) {
            insertAt -= 1;
        }
        const reorderedList = [...nextSourceList];
        reorderedList.splice(Math.min(insertAt, reorderedList.length), 0, field);
        return {
            sourceList: reorderedList,
            targetList: reorderedList,
        };
    }

    const nextTargetList = [...targetList];
    nextTargetList.splice(Math.min(insertAt, nextTargetList.length), 0, field);
    return {
        sourceList: nextSourceList,
        targetList: nextTargetList,
    };
};

export const getDefaultQuery = () => ({
    'start-date': '',
    'end-date': '',
    app: 0,
    site: 0,
    log_type: DEFAULT_LOG_TYPE,
    api_type: TRINO_API_TYPE,
    is_export_admin_mode: false,
    use_realtime: true,
    use_data_censoring: true,
    admin_mode_toggled: false,
    device_id: '',
    text_search: '',
    conversion_type_filter: null,
    include_events: true,
    include_agencies: true,
    include_countries: true,
    include_partners: true,
    include_postback_attributed_partners: true,
});

export const resetQueryForLogType = (query, logType, operations) => ({
    ...query,
    log_type: logType,
    partner_ids: [],
    partners: [],
    postback_attributed_partner_ids: [],
    postback_attributed_partners: [],
    events: [],
    countries: [],
    agencies: [],
    device_id: '',
    text_search: '',
    conversion_type_filter: logType === INSTALLS_LOG_TYPE ? 'all' : undefined,
    include_events: operations.events.name === INCLUDE,
    include_agencies: operations.agencies.name === INCLUDE,
    include_countries: operations.countries.name === INCLUDE,
    include_partners: operations.partners.name === INCLUDE,
    include_postback_attributed_partners: operations.postbackAttributedPartners.name === INCLUDE,
});

export const getDisabledFilters = (apsalarEnabled, apiType, logType) => ({
    partners: apsalarEnabled && apiType !== TRINO_API_TYPE && logType === INSTALLS_LOG_TYPE,
    countries:
        apsalarEnabled &&
        (apiType !== TRINO_API_TYPE || (logType !== DEFAULT_LOG_TYPE && logType !== INSTALLS_LOG_TYPE)),
    agencies: !apsalarEnabled || (apsalarEnabled && logType === FPS_LOG_TYPE),
    events:
        apsalarEnabled &&
        (apiType !== TRINO_API_TYPE || (logType !== DEFAULT_LOG_TYPE && logType !== POSTBACKS_LOG_TYPE)),
});

export const getMinDate = ({
    userData = {},
    apiTypeToMinDate = {},
    logType,
    allowAdminFullRange = false,
    adminMode,
}) => {
    if (adminMode && allowAdminFullRange) {
        return undefined;
    }

    let minDate;
    if (userData.allowLongPeriodExports || userData.allow_long_period_exports) {
        const exportType = EXPORT_TYPE_MAPPING[logType];
        minDate = apiTypeToMinDate[exportType] || apiTypeToMinDate[TRINO_API_TYPE];
    } else {
        minDate = moment()
            .subtract(1, 'months')
            .startOf('month')
            .format('YYYY-MM-DD');
    }

    return minDate;
};

export const getTaskLink = ({ taskId, logType, apiType }) => {
    let link = `/logs/download?log_type=${logType}&api_type=${apiType}&task_id=${taskId}`;
    const impersonationUsername = getImpersonationFromQuery();
    if (impersonationUsername) {
        link += `&user=${decodeURIComponent(impersonationUsername)}`;
    }
    const customerId = getImpersonationFromQuery('customer_id');
    if (customerId) {
        link += `&customer_id=${customerId}`;
    }
    return link;
};

export const normalizeTagOption = (item, labelKey, valueKey = labelKey) => ({
    ...item,
    label: item[labelKey],
    value: item[valueKey],
});

export const buildTagSuggestions = (items = [], labelKey, valueKey = labelKey) =>
    items.map(item => normalizeTagOption(item, labelKey, valueKey));

export const buildPreparedQuery = ({ query, selected, operations, adminMode, selectedFields }) => {
    const prepared = {
        ...query,
        force_no_realtime: !query.use_realtime,
        use_data_censoring: query.use_data_censoring,
        admin_mode_toggled: adminMode,
        app: selected.app?.app_id,
        site: selected.site?.site_id,
        device_id_type: selected.deviceIdType?.name,
        postback_type:
            selected.postbackType?.name && selected.postbackType.name !== 'choose postback type'
                ? selected.postbackType.name
                : null,
        limit_result: selected.limitResult?.name,
        user_selected_fields: JSON.stringify(selectedFields),
    };

    if (selected.partners?.length) {
        prepared.partner_ids = selected.partners.map(partner => partner.id);
        prepared.partners = selected.partners.map(({ apsalar_id: apsalarId, id }) => ({ apsalar_id: apsalarId, id }));
        prepared.include_partners = operations.partners.name === INCLUDE;
    }

    if (selected.postbackAttributedPartners?.length) {
        prepared.postback_attributed_partner_ids = selected.postbackAttributedPartners.map(partner => partner.id);
        prepared.postback_attributed_partners = selected.postbackAttributedPartners.map(
            ({ apsalar_id: apsalarId, id }) => ({
                apsalar_id: apsalarId,
                id,
            })
        );
        prepared.include_postback_attributed_partners = operations.postbackAttributedPartners.name === INCLUDE;
    }

    if (selected.events?.length) {
        prepared.events = selected.events.map(event => event.name);
        prepared.include_events = operations.events.name === INCLUDE;
    }

    if (selected.countries?.length) {
        prepared.countries = selected.countries.map(country => country.country_code);
        prepared.include_countries = operations.countries.name === INCLUDE;
    }

    if (selected.agencies?.length) {
        prepared.agencies = selected.agencies.map(agency => agency.agency_id);
        prepared.include_agencies = operations.agencies.name === INCLUDE;
    }

    if (moment(prepared['end-date']).isAfter(moment())) {
        prepared['end-date'] = moment()
            .subtract(1, 'day')
            .format('YYYY-MM-DD');
    }

    if (prepared.device_id) {
        prepared.device_id = prepared.device_id.toLowerCase();
    }

    return prepared;
};

export const getBasicDeviceIdTypes = deviceIdTypes =>
    deviceIdTypes.filter(deviceIdType => BASIC_DEVICE_ID_TYPES.includes(deviceIdType.name));

export const getDeviceIdTypeOptions = ({ deviceIdTypes = [], logType, adminMode }) => {
    if (logType === FPS_LOG_TYPE) {
        return deviceIdTypes.filter(deviceIdType => FPS_DEVICE_ID_TYPES.includes(deviceIdType.name));
    }

    return adminMode ? deviceIdTypes : getBasicDeviceIdTypes(deviceIdTypes);
};

export const getBasicPostbackTypes = postbackTypes =>
    postbackTypes.filter(postbackType => !BASIC_POSTBACK_TYPES_BLOCKLIST.includes(postbackType.display_name));

export const canEditDeviceId = logType => logType !== SKAN_POSTBACK_LOG_TYPE;

export const getAvailableLogTypes = ({
    queryApiType,
    adminMode,
    trinoEventTypes = [],
    trinoEventTypesAdminMode = [],
    oldEventTypes = [],
}) => {
    if (queryApiType === TRINO_API_TYPE) {
        return adminMode ? trinoEventTypesAdminMode : trinoEventTypes;
    }
    return oldEventTypes;
};
